/**
 * Who is signed in. Staff sign in with trtCRM (OpenID Connect); the whole exchange happens on the
 * server (backend/lib/oidc.mjs) and leaves the browser with one httpOnly session cookie it can
 * neither read nor forge. This module only asks the server who that is (GET /auth/session) and
 * keeps the answer for the UI: the name and picture to show, and the CSRF token that
 * state-changing requests must send back. No token ever reaches JavaScript.
 *
 * A signed-out visitor is a guest: they can join any room they have the link to, and wait to be
 * admitted. Hosting (starting a room, admitting guests) takes a session.
 */

let user = null;
let csrf = null;
let expiresAt = null;
let calendar = false;
let ai = false;
const listeners = new Set();

function notify() {
  for (const fn of [...listeners]) {
    try {
      fn(user);
    } catch {
      /* a listener must not break auth */
    }
  }
}

function adopt(body) {
  const next = body?.user ?? null;
  const changed = (next?.sub ?? null) !== (user?.sub ?? null);
  user = next;
  csrf = next ? (body.csrf ?? null) : null;
  expiresAt = next ? (body.expiresAt ?? null) : null;
  calendar = next ? body.calendar === true : false;
  ai = next ? body.ai === true : false;
  if (changed) notify();
}

/**
 * Ask the server who is signed in. Resolves the user, or null. A server that cannot be reached
 * leaves what is already known (the page keeps working for a guest and for a call in progress).
 */
export async function loadSession() {
  try {
    const res = await fetch('/auth/session', { credentials: 'same-origin', cache: 'no-store' });
    if (res.status === 404) {
      adopt(null); // this deployment has no sign-in
      return null;
    }
    if (!res.ok) return user;
    adopt(await res.json());
  } catch {
    /* offline, or no server (a test): keep what we know */
  }
  return user;
}

/** Call once at boot, before routing. */
export async function initAuth() {
  await loadSession();
}

/** @returns {{ sub: string, name: string, email: string, picture: string, staffId: number|null, admin: boolean } | null} */
export function getUser() {
  return user;
}

export function isSignedIn() {
  return user !== null;
}

/** Whether the server holds a CRM token for this member right now (so their calendar can be read). */
export function calendarReady() {
  return calendar;
}

/** Whether the meeting assistant (summary, questions) is switched on. */
export function aiReady() {
  return ai;
}

/** When the server-side session lapses unless the CRM is asked again (ms since the epoch). */
export function sessionExpiresAt() {
  return expiresAt;
}

/** Headers state-changing requests to the backend must carry while signed in. */
export function authHeader() {
  return csrf ? { 'X-CSRF-Token': csrf } : {};
}

/** Subscribe to sign-in/out. Returns an unsubscribe function. */
export function onAuthChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Test hook: act as this user (or nobody) without a server. */
export function setUserForTests(next, token = 'test-csrf') {
  adopt(next ? { user: next, csrf: token, expiresAt: Date.now() + 20 * 60_000 } : null);
}

/** Where the browser goes after signing in: this page, as an absolute path. */
function currentPath(win) {
  const hash = win.location.hash;
  const room = /^#\/m\/(.+)$/.exec(hash);
  return room ? `/${room[1]}` : (win.location.pathname || '/');
}

/**
 * Go to trtCRM to sign in; the CRM sends the browser back here. `silent` asks without showing the
 * CRM's login page (it answers at once: signed in, or `login_required`).
 * @returns {'redirected'}
 */
export function login({ silent = false, win = typeof window !== 'undefined' ? window : null } = {}) {
  if (!win) return 'unavailable';
  const url = new URL('/login', win.location.origin);
  url.searchParams.set('return', currentPath(win));
  if (silent) url.searchParams.set('silent', '1');
  win.location.assign(url.href);
  return 'redirected';
}

/** Alias kept for the views: "ask the CRM to sign this person in". */
export const requestSignIn = login;

/**
 * Sign out of Meet: clear the app's session and stay on this site (home). The CRM session is NOT touched, so the person is still
 * signed in to the CRM and can come back with one click on Sign in. Pass `everywhere` to end the CRM session too.
 */
export async function signOut(win = typeof window !== 'undefined' ? window : null, { everywhere = false } = {}) {
  let redirect = '/';
  try {
    const res = await fetch(`/auth/logout${everywhere ? '?everywhere=1' : ''}`, { method: 'POST', credentials: 'same-origin', headers: authHeader() });
    if (res.ok) redirect = (await res.json()).redirect ?? '/';
  } catch {
    /* the session cookie lapses on its own; still leave */
  }
  adopt(null);
  if (win) win.location.assign(redirect);
}

/** The CRM ended the session (or refused the account): forget the user without leaving the page. */
export function endSession() {
  adopt(null);
}

/** Why a sign-in came back unsuccessful, read from `?auth_error=` and removed from the address bar. */
export function takeAuthError(win = typeof window !== 'undefined' ? window : null) {
  if (!win) return null;
  const params = new URLSearchParams(win.location.search);
  const code = params.get('auth_error');
  if (!code) return null;
  params.delete('auth_error');
  const query = params.toString();
  try {
    win.history.replaceState(null, '', win.location.pathname + (query ? `?${query}` : '') + win.location.hash);
  } catch {
    /* ignore */
  }
  return /^[a-z_]{1,40}$/.test(code) ? code : 'invalid';
}

/** What to tell the person about each way a sign-in can fail. */
export function authErrorMessage(code) {
  switch (code) {
    case 'access_denied':
      return 'trtCRM did not let this account in. Only active team members can sign in.';
    case 'login_required':
      return 'Your trtCRM session has ended. Sign in again.';
    case 'unavailable':
      return 'trtCRM could not be reached. Try again in a moment.';
    default:
      return 'Sign-in did not complete. Try again.';
  }
}
